/* Side cart (Kt) — the 2026 cart drawer behaviour. */

const KT_CART_SECTION_ID = 'kt-cart-drawer';

const KT_ACCORDION_EVENT = 'kt:cart-accordion-toggle';

const KT_ADDON_ACCORDION_NAME = 'gift-bag';

const KT_DN_CART_EVENTS = ['la:dn:cart:updated', 'la:dn:cart:discounted'];

const KT_DN_PROCESSED_EVENT = 'la:dn:cart-mutations:processed';

const KT_DN_GRACE_TIMEOUT = 400;
const KT_DN_SETTLE_TIMEOUT = 1200;

const KT_DN_PAINT_TIMEOUT = 2000;

const KT_DN_LOAD_GRACE = 4000;

const KT_ATTACHMENTS = [
  {
    group: '_Embroidery Group Number',
    marker: '_Embroidery Item',
    cascadesFromAttachment: true,
  },
  {
    group: '_Gifting Group',
    marker: '_Gifting Item',
    cascadesFromAttachment: false,
  },
];

function ktProperties(item) {
  return (item && item.properties) || {};
}

class KtCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.overlay = this.querySelector('.kt-cart-drawer__overlay');
    this.panel = this.querySelector('.kt-cart-drawer__panel');
    this.activeElement = null;

    this.pendingWrites = 0;

    this.loadingLine = null;

    this.addonOpen = false;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.addEventListener(
      'change',
      debounce((event) => {
        const input = event.target.closest('.kt-quantity__input');
        if (!input) return;

        const active = document.activeElement;
        const pressed = active && active.closest('.kt-cart-item__quantity') ? active : input;

        this.updateQuantity(input.dataset.index, input.value, pressed);
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );

    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-kt-cart-add]');
      if (!form) return;
      event.preventDefault();
      this.addFromForm(form);
    });

    this.addonAccordionHandler = (event) => {
      if (event.detail && event.detail.name === KT_ADDON_ACCORDION_NAME) return;
      this.setAddonOpen(false);
    };
    document.addEventListener(KT_ACCORDION_EVENT, this.addonAccordionHandler);

    this.bindHeaderIcon();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'kt-cart-drawer' || event.source === 'product-form') return;
      this.refresh();
      this.reconcileWithDiscountNinja();
    });

    this.bindDiscountNinja();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.unbindDiscountNinja();
    if (this.addonAccordionHandler) document.removeEventListener(KT_ACCORDION_EVENT, this.addonAccordionHandler);
  }

  bindHeaderIcon() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  onClick(event) {
    if (event.target.closest('[data-kt-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const removeButton = event.target.closest('[data-kt-cart-remove]');
    if (removeButton) {
      event.preventDefault();
      this.updateQuantity(removeButton.dataset.index, 0, removeButton);
      return;
    }

    const checkout = event.target.closest('[data-kt-cart-checkout]');
    if (checkout) {
      this.checkout(event, checkout);
      return;
    }

    const addonToggle = event.target.closest('[data-kt-cart-addon-toggle]');
    if (addonToggle) this.setAddonOpen(!this.addonOpen);
  }

  setAddonOpen(open) {
    this.addonOpen = open;
    this.applyAddonOpenState();

    if (open) {
      document.dispatchEvent(new CustomEvent(KT_ACCORDION_EVENT, { detail: { name: KT_ADDON_ACCORDION_NAME } }));
    }
  }

  applyAddonOpenState() {
    const wrapper = this.querySelector('[data-kt-cart-addon]');

    if (!wrapper) {
      this.addonOpen = false;
      return;
    }

    if (this.addonOpen) {
      wrapper.setAttribute('open', '');
    } else {
      wrapper.removeAttribute('open');
    }

    const toggle = wrapper.querySelector('[data-kt-cart-addon-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', this.addonOpen ? 'true' : 'false');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.withholdPrices();

    // Deferred a frame: the panel is display:none until `active` lands, and a
    // transform set in the same frame as the display change does not transition.
    // Same workaround as the theme's own cart-drawer.js.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        trapFocus(this, this.panel);
      },
      { once: true }
    );

    this.notifyDiscountNinja();

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [{ id: KT_CART_SECTION_ID }, { id: 'cart-icon-bubble' }];
  }

  renderContents(parsedState) {
    if (parsedState && parsedState.sections) {
      this.renderSections(parsedState.sections);
      this.reconcileWithDiscountNinja();
      this.open();
    } else {
      this.refresh().then(() => this.open());
    }
  }

  renderSections(sections) {
    const markup = sections[KT_CART_SECTION_ID];
    if (!markup) return;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');

    ['KtCartDrawerBody', 'KtCartDrawerAddons', 'KtCartDrawerFooter'].forEach((id) => {
      const source = parsed.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    this.applyAddonOpenState();

    const emptied = parsed.querySelector('cart-drawer');
    if (emptied) this.classList.toggle('is-empty', emptied.classList.contains('is-empty'));

    this.renderRecommendationsKey(parsed);
    this.renderCartCount(sections['cart-icon-bubble']);

    this.notifyDiscountNinja();

    if (this.dnSettleHandler && this.loadingLine !== null) this.enableLoading(this.loadingLine);
  }

  renderRecommendationsKey(parsed) {
    const recommendations = this.querySelector('kt-cart-recommendations');
    const source = parsed.querySelector('kt-cart-recommendations');
    if (!recommendations || !source) return;

    ['data-product-id', 'data-cart-products'].forEach((attribute) => {
      recommendations.setAttribute(attribute, source.getAttribute(attribute) || '');
    });
  }

  renderCartCount(markup) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || !markup) return;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const source = parsed.querySelector('.cart-count-bubble');
    const existing = cartLink.querySelector('.cart-count-bubble');

    if (source && existing) {
      existing.replaceWith(source);
    } else if (source) {
      cartLink.appendChild(source);
    } else if (existing) {
      existing.remove();
    }
  }

  refresh() {
    const url = `${window.location.pathname}?sections=${this.getSectionsToRender()
      .map((section) => section.id)
      .join(',')}`;

    return fetch(url)
      .then((response) => response.json())
      .then((sections) => this.renderSections(sections))
      .catch((error) => console.error('[kt-cart-drawer] refresh failed', error));
  }

  addFromForm(form) {
    const button = form.querySelector('button[type="submit"]');
    if (button) button.setAttribute('aria-disabled', 'true');

    this.pendingWrites += 1;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const body = new FormData(form);
    body.append('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    body.append('sections_url', window.location.pathname);
    config.body = body;

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.showError(response.description || response.message);
          return;
        }
        this.renderSections(response.sections || {});
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kt-cart-drawer' });
        this.reconcileWithDiscountNinja();
      })
      .catch((error) => {
        console.error('[kt-cart-drawer] add failed', error);
        this.showError(window.cartStrings && window.cartStrings.error);
      })
      .finally(() => {
        this.pendingWrites -= 1;
        if (button) button.removeAttribute('aria-disabled');
      });
  }

  updateQuantity(line, quantity, sourceElement) {
    this.enableLoading(line);
    this.loadingLine = Number(quantity) > 0 ? line : null;
    this.clearError();

    this.pendingWrites += 1;

    const sectionIds = this.getSectionsToRender().map((section) => section.id);

    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartData) => {
        const item = cartData.items[line - 1];
        const newQuantity = parseInt(quantity, 10);
        const properties = ktProperties(item);

        const intended = new Map();
        cartData.items.forEach((other, index) => {
          intended.set(other.key, index === line - 1 ? newQuantity : other.quantity);
        });

        if (newQuantity === 0) {
          KT_ATTACHMENTS.forEach(({ group, marker, cascadesFromAttachment }) => {
            if (!cascadesFromAttachment) return;

            const groupValue = properties[group];
            if (!groupValue || properties[marker] !== 'true') return;

            cartData.items.forEach((other) => {
              if (ktProperties(other)[group] === groupValue) intended.set(other.key, 0);
            });
          });
        }

        KT_ATTACHMENTS.forEach(({ group, marker }) => {
          const parentTotals = new Map();

          cartData.items.forEach((other) => {
            const otherProperties = ktProperties(other);
            const groupValue = otherProperties[group];
            if (!groupValue || otherProperties[marker] === 'true') return;
            parentTotals.set(groupValue, (parentTotals.get(groupValue) || 0) + intended.get(other.key));
          });

          cartData.items.forEach((other) => {
            const otherProperties = ktProperties(other);
            const groupValue = otherProperties[group];
            if (!groupValue || otherProperties[marker] !== 'true') return;
            if (other.key === (item && item.key)) return;
            if (!parentTotals.has(groupValue)) return;
            intended.set(other.key, parentTotals.get(groupValue));
          });
        });

        const updates = {};
        let movedOthers = false;
        cartData.items.forEach((other) => {
          const to = intended.get(other.key);
          if (to === other.quantity) return;
          updates[other.key] = to;
          if (other.key !== (item && item.key)) movedOthers = true;
        });

        const url = movedOthers ? routes.cart_update_url : routes.cart_change_url;
        const payload = movedOthers
          ? { updates, sections: sectionIds, sections_url: window.location.pathname }
          : { line, quantity, sections: sectionIds, sections_url: window.location.pathname };

        return fetch(url, { ...fetchConfig(), body: JSON.stringify(payload) }).then((response) =>
          response.json()
        );
      })
      .then((state) => {
        if (state.errors) {
          this.showError(state.errors);
          this.refresh();
          return;
        }

        this.renderSections(state.sections || {});
        this.announceQuantity(state, line, quantity);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kt-cart-drawer' });
        this.reconcileWithDiscountNinja();

        this.restoreFocus(line, sourceElement);
      })
      .catch((error) => {
        console.error('[kt-cart-drawer] quantity update failed', error);
        this.showError(window.cartStrings && window.cartStrings.error);
      })
      .finally(() => {
        this.pendingWrites -= 1;
        this.disableLoading();
      });
  }

  announceQuantity(state, line, requested) {
    const region = document.getElementById('KtCartDrawerLiveRegion');
    if (!region || !window.cartStrings) return;

    const item = state.items && state.items[line - 1];
    const actual = item ? item.quantity : undefined;
    if (actual === parseInt(requested, 10)) return;

    region.textContent =
      typeof actual === 'undefined'
        ? window.cartStrings.error
        : window.cartStrings.quantityError.replace('[quantity]', actual);
  }

  restoreFocus(line, sourceElement) {
    const name = sourceElement && sourceElement.getAttribute('name');
    const row = document.getElementById(`KtCartItem-${line}`);
    const target = (name && row && row.querySelector(`[name="${name}"]`)) || this.panel;
    trapFocus(this, target);
  }

  showError(message) {
    const errors = document.getElementById('KtCartDrawerErrors');
    if (errors) errors.textContent = message || '';
  }

  clearError() {
    this.showError('');
  }

  enableLoading(line) {
    this.classList.add('kt-cart-drawer--loading');
    const row = document.getElementById(`KtCartItem-${line}`);
    if (row) row.querySelectorAll('.loading-overlay').forEach((el) => el.classList.remove('hidden'));
  }

  disableLoading() {
    if (this.dnSettleHandler) return;

    this.loadingLine = null;
    this.classList.remove('kt-cart-drawer--loading', 'kt-cart-drawer--settling');
    this.querySelectorAll('.loading-overlay').forEach((el) => el.classList.add('hidden'));
  }

  bindDiscountNinja() {
    this.dnCartHandler = this.onDiscountNinjaCart.bind(this);
    KT_DN_CART_EVENTS.forEach((name) => document.addEventListener(name, this.dnCartHandler));

    this.dnReadyHandler = this.notifyDiscountNinja.bind(this);
    document.addEventListener('la:dn:api:ready', this.dnReadyHandler);

    // The hook for Discount Ninja's own refresh. When an offer adds a gift or
    // splits a line the app changes the cart itself and reloads the page by
    // default; Settings > General > Advanced > Refresh behavior takes this line
    // instead: window.ktCartDrawerRefresh && window.ktCartDrawerRefresh();
    window.ktCartDrawerRefresh = () => this.refresh();
  }

  unbindDiscountNinja() {
    if (this.dnCartHandler) {
      KT_DN_CART_EVENTS.forEach((name) => document.removeEventListener(name, this.dnCartHandler));
    }
    if (this.dnReadyHandler) document.removeEventListener('la:dn:api:ready', this.dnReadyHandler);
    this.cancelDiscountNinjaReconcile();
    this.releasePrices();
    if (window.ktCartDrawerRefresh) delete window.ktCartDrawerRefresh;
  }

  notifyDiscountNinja() {
    if (this.dnNotifyScheduled) return;
    this.dnNotifyScheduled = true;

    requestAnimationFrame(() => {
      this.dnNotifyScheduled = false;
      this.withholdPrices();
      document.dispatchEvent(new CustomEvent('la:dn:drawercart:opened'));
    });
  }

  withholdPrices() {
    const loaded = typeof discountNinja !== 'undefined';

    const age = window.performance && window.performance.now ? window.performance.now() : 0;
    const stillArriving = !loaded && age < KT_DN_LOAD_GRACE;
    if (!loaded && !stillArriving) return;

    this.releasePrices();
    this.classList.add('kt-cart-drawer--pricing');

    this.dnPaintObserver = new MutationObserver((records) => {
      const painted = records.some(
        (record) =>
          record.target.closest &&
          record.target.closest('.kt-cart-item__prices, .kt-cart-totals__value')
      );
      if (painted) this.releasePrices();
    });

    this.dnPaintObserver.observe(this.panel, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    const wait = loaded
      ? KT_DN_PAINT_TIMEOUT
      : KT_DN_LOAD_GRACE - age + KT_DN_PAINT_TIMEOUT;

    this.dnPaintTimer = setTimeout(() => this.releasePrices(), wait);

    this.dnPaintToken = (this.dnPaintToken || 0) + 1;
    const token = this.dnPaintToken;
    const checkout = loaded && discountNinja.api && discountNinja.api.checkout;
    if (!checkout || typeof checkout.isDiscounted !== 'function') return;

    Promise.resolve(checkout.isDiscounted())
      .then((isDiscounted) => {
        if (isDiscounted) return;
        if (token !== this.dnPaintToken) return;
        this.releasePrices();
      })
      .catch(() => {
      });
  }

  releasePrices() {
    if (this.dnPaintObserver) {
      this.dnPaintObserver.disconnect();
      this.dnPaintObserver = null;
    }
    if (this.dnPaintTimer) {
      clearTimeout(this.dnPaintTimer);
      this.dnPaintTimer = null;
    }

    this.classList.remove('kt-cart-drawer--pricing');
  }

  onDiscountNinjaCart(event) {
    if (this.dnSettleHandler) return;

    const cart = event && event.detail && event.detail.data && event.detail.data[0];
    if (!cart) return;

    const total = cart.total && cart.total.discountedPrice;
    if (!Number.isFinite(total)) return;
    const lines = Array.isArray(cart.items) ? cart.items.length : null;

    const rendered = this.renderedCartFigures();
    if (!rendered) return;

    const signature = `${total}:${lines}:${rendered.total}:${rendered.lines}`;
    if (this.dnSignature === signature) return;
    this.dnSignature = signature;

    if (total === rendered.total && (lines === null || lines === rendered.lines)) {
      this.notifyDiscountNinja();
      return;
    }

    this.refresh();
  }

  renderedCartFigures() {
    const marker = document.querySelector('#KtCartDrawerFooter [data-kt-cart-total]');
    if (!marker) return null;

    return {
      total: Number(marker.dataset.ktCartTotal),
      lines: Number(marker.dataset.ktCartLines),
    };
  }

  reconcileWithDiscountNinja() {
    if (typeof discountNinja === 'undefined') return;

    this.cancelDiscountNinjaReconcile();

    this.classList.add('kt-cart-drawer--settling');

    const settle = () => {
      this.cancelDiscountNinjaReconcile();

      if (this.pendingWrites > 0) return;

      this.refresh().finally(() => this.disableLoading());
    };

    this.dnSettleHandler = settle;
    KT_DN_CART_EVENTS.forEach((name) => document.addEventListener(name, settle, { once: true }));

    this.dnProcessedHandler = () => {
      if (this.dnGraceTimer) return;
      this.dnGraceTimer = setTimeout(settle, KT_DN_GRACE_TIMEOUT);
    };
    document.addEventListener(KT_DN_PROCESSED_EVENT, this.dnProcessedHandler);

    this.dnSettleTimer = setTimeout(settle, KT_DN_SETTLE_TIMEOUT);
  }

  cancelDiscountNinjaReconcile() {
    if (this.dnSettleTimer) clearTimeout(this.dnSettleTimer);
    this.dnSettleTimer = null;

    if (this.dnGraceTimer) clearTimeout(this.dnGraceTimer);
    this.dnGraceTimer = null;

    if (this.dnSettleHandler) {
      KT_DN_CART_EVENTS.forEach((name) =>
        document.removeEventListener(name, this.dnSettleHandler)
      );
      this.dnSettleHandler = null;
    }

    if (this.dnProcessedHandler) {
      document.removeEventListener(KT_DN_PROCESSED_EVENT, this.dnProcessedHandler);
      this.dnProcessedHandler = null;
    }
  }

  checkout(event, link) {
    const dn = typeof discountNinja === 'undefined' ? null : discountNinja;
    const api = dn && dn.api && dn.api.checkout;
    if (!api || typeof api.isDiscounted !== 'function') return;

    event.preventDefault();

    Promise.resolve(api.isDiscounted())
      .then((isDiscounted) => {
        if (isDiscounted) {
          document.dispatchEvent(new CustomEvent('la:dn:checkout:initiate'));
        } else {
          window.location.href = link.href;
        }
      })
      .catch((error) => {
        console.error('[kt-cart-drawer] checkout handover failed', error);
        window.location.href = link.href;
      });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', KtCartDrawer);
}
